import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const CABLE_COLORS = {
  negro: '#334155',
  gris: '#94a3b8',
  blanco: '#e2e8f0',
  rojo: '#ef4444',
  azul: '#3b82f6',
  verde: '#22c55e',
  amarillo: '#eab308',
  naranja: '#f97316',
  violeta: '#8b5cf6',
  celeste: '#38bdf8',
};

function cableHex(color) {
  if (!color) return '#64748b';
  return CABLE_COLORS[String(color).toLowerCase()] || '#64748b';
}

function asArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function normalizePort(port, idx) {
  const conn = port.connection || port.active_connection || null;
  const other = conn?.other_end || conn?.peer || {};

  const localLabel =
    port.label
    || port.name
    || port.interface_name
    || (port.port_number != null ? `P${port.port_number}` : `Port ${idx + 1}`);

  const peerName =
    other.element_name
    || other.device_name
    || other.name
    || conn?.remote_element_name
    || conn?.peer_name
    || null;

  const peerPort =
    other.label
    || other.interface_name
    || (other.port_number != null ? `P${other.port_number}` : null)
    || conn?.remote_port_label
    || conn?.peer_port_label
    || null;

  const connected = Boolean(conn) || Boolean(port.is_connected && (peerName || peerPort));

  return {
    id: port.id || `tmp-${idx}`,
    portNumber: port.port_number,
    localLabel,
    localType: port.port_type || port.connection_type || port.component_type || null,
    localSpeed: port.speed || null,
    componentType: port.component_type || null,
    connection: conn,
    peerName,
    peerPort,
    connected,
  };
}

function NetworkDiagram({ rows }) {
  if (!rows.length) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', fontStyle: 'italic', padding: '0.75rem 0' }}>
        Sin puertos de red en este CI.
      </div>
    );
  }

  const connected = rows.filter(r => r.connected);
  if (!connected.length) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', fontStyle: 'italic', padding: '0.75rem 0' }}>
        Hay puertos de red, pero ninguno conectado.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', padding: '0.75rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: 2 }}>
        <div style={{ minWidth: 140, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>Puerto local</div>
        <div style={{ flex: 1 }} />
        <div style={{ minWidth: 180, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>Destino</div>
      </div>

      {connected.map((r) => {
        const color = cableHex(r.connection?.cable_color);
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'rgba(59,130,246,0.12)',
              border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: 6,
              padding: '0.3rem 0.6rem',
              fontSize: '0.78rem',
              color: '#60a5fa',
              fontWeight: 600,
              minWidth: 140,
              textAlign: 'center',
            }}>
              {r.localLabel}
              {r.teamName && (
                <div style={{ fontSize: '0.66rem', color: '#c4b5fd', fontWeight: 500, marginTop: 1 }}>team: {r.teamName}</div>
              )}
            </div>

            <div style={{ flex: 1, position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
              <div style={{ height: 2, width: '100%', background: `linear-gradient(90deg, rgba(59,130,246,0.45), ${color}, rgba(16,185,129,0.45))`, borderRadius: 1 }} />
              {(r.connection?.cable_type || r.connection?.cable_color) && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--bg-surface)',
                  border: `1px solid ${color}`,
                  borderRadius: 3,
                  padding: '1px 6px',
                  fontSize: '0.67rem',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}>
                  {[r.connection?.cable_type, r.connection?.cable_color].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>

            <div style={{
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 6,
              padding: '0.3rem 0.6rem',
              fontSize: '0.78rem',
              color: '#10b981',
              fontWeight: 600,
              minWidth: 180,
              textAlign: 'center',
            }}>
              {r.peerName || 'Destino'}
              {r.peerPort && (
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 500, marginTop: 1 }}>{r.peerPort}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function NetworkConnectionsPanel({
  objectType = 'rack_element',
  objectId,
  companyId,
  elementName,
  teams = [],
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // table | graph

  const loadPorts = useCallback(async () => {
    if (!objectId) return;

    setLoading(true);
    setError(null);
    try {
      let response;
      if (objectType === 'router') response = await api.getRouterPorts(objectId, companyId);
      else if (objectType === 'firewall') response = await api.getFirewallPorts(objectId, companyId);
      else if (objectType === 'workstation') response = await api.getWorkstationPorts(objectId, companyId);
      else response = await api.getPorts(objectId, companyId);

      const list = asArray(response)
        .filter(p => p?.component_type !== 'psu')
        .filter(p => p?.port_kind !== 'power')
        .map(normalizePort)
        .sort((a, b) => {
          if (a.portNumber != null && b.portNumber != null) return Number(a.portNumber) - Number(b.portNumber);
          return String(a.localLabel).localeCompare(String(b.localLabel));
        });

      setRows(list);
    } catch (e) {
      setError(e.message || 'Error cargando conexiones de red');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [objectId, objectType, companyId]);

  useEffect(() => { loadPorts(); }, [loadPorts]);

  const teamByPort = useMemo(() => {
    const m = new Map();
    (Array.isArray(teams) ? teams : []).forEach((t) => {
      (t.member_port_ids || []).forEach((pid) => m.set(Number(pid), t.name));
    });
    return m;
  }, [teams]);

  const displayRows = useMemo(() => {
    return rows.map(r => ({ ...r, teamName: teamByPort.get(Number(r.id)) || null }));
  }, [rows, teamByPort]);

  const connectedCount = displayRows.filter(r => r.connected).length;

  if (!objectId) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1rem 0' }}>
        Guarda el CI primero para ver su mapa de conexiones de red.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 className="dv-section-title" style={{ margin: 0 }}>Mapa de conexiones de red</h3>
          {elementName && <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{elementName}</span>}
          <span style={{ background: 'rgba(59,130,246,0.15)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 999, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
            {connectedCount}/{displayRows.length} conectados
          </span>
          {Array.isArray(teams) && teams.length > 0 && (
            <span style={{ background: 'rgba(139,92,246,0.15)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.35)', borderRadius: 999, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
              {teams.length} teams
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 7,
              padding: '0.32rem 0.62rem',
              fontSize: '0.74rem',
              cursor: 'pointer',
              background: viewMode === 'table' ? 'rgba(59,130,246,0.15)' : 'var(--bg-input)',
              color: viewMode === 'table' ? '#93c5fd' : 'var(--text-secondary)',
              fontWeight: 600,
            }}
          >
            Cuadricula
          </button>
          <button
            type="button"
            onClick={() => setViewMode('graph')}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 7,
              padding: '0.32rem 0.62rem',
              fontSize: '0.74rem',
              cursor: 'pointer',
              background: viewMode === 'graph' ? 'rgba(16,185,129,0.15)' : 'var(--bg-input)',
              color: viewMode === 'graph' ? '#6ee7b7' : 'var(--text-secondary)',
              fontWeight: 600,
            }}
          >
            Diagrama
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: 8 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.84rem' }}>Cargando conexiones de red...</div>
      ) : viewMode === 'graph' ? (
        <NetworkDiagram rows={displayRows} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem', color: 'var(--text-muted)' }}>Puerto</th>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem', color: 'var(--text-muted)' }}>Tipo</th>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem', color: 'var(--text-muted)' }}>Team</th>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem', color: 'var(--text-muted)' }}>Estado</th>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem', color: 'var(--text-muted)' }}>Conectado a</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--text-muted)', fontStyle: 'italic', padding: '0.8rem 0.5rem' }}>
                    Sin puertos de red.
                  </td>
                </tr>
              ) : displayRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                  <td style={{ padding: '0.42rem 0.5rem', color: 'var(--text-primary)', fontWeight: 600 }}>{r.localLabel}</td>
                  <td style={{ padding: '0.42rem 0.5rem', color: 'var(--text-secondary)' }}>{r.localType || r.componentType || '-'}</td>
                  <td style={{ padding: '0.42rem 0.5rem' }}>
                    {r.teamName ? (
                      <span style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.35)', color: '#c4b5fd', borderRadius: 999, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700 }}>
                        {r.teamName}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.42rem 0.5rem' }}>
                    {r.connected
                      ? <span style={{ color: '#34d399', fontWeight: 600 }}>Conectado</span>
                      : <span style={{ color: '#94a3b8' }}>Libre</span>}
                  </td>
                  <td style={{ padding: '0.42rem 0.5rem', color: 'var(--text-secondary)' }}>
                    {r.connected
                      ? `${r.peerName || 'Destino'}${r.peerPort ? ` (${r.peerPort})` : ''}`
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
